// Window.ts — PURE procedural window model for the Buildings Set1 opening layer (§4.4). The model
// is authored in the OPENING-SEGMENT LOCAL frame:
//
//     x = along the wall (0 = the opening's center),
//     y = UP (0 = the window's SILL — the opening's zBottom),
//     z = OUTWARD (0 = the interior face of the wall),
//
// and then transformed into building-local coordinates by the caller (via the segment frame
// basis). The frame spans the opening [−w/2, w/2] × [0, height]; a projecting `sill` sits under
// the opening on the exterior (outward) side.
//
// Parts (§4.4):
//   - FRAME: 4 casing boxes filling the FULL `wallThickness` reveal (D1 analog — same decision as
//     the door: the frame lining always spans the whole wall depth, so the reveal is never left as
//     an empty box and `frameDepth` is unnecessary). The head + sill casing span ONLY between the
//     jambs so the four corners are not double-covered (bug F analog), tagged `windowFrame`.
//   - SASH: an inner frame ring (4 boxes, top/bottom rails spanning only between the stiles) slightly
//     inset from the casing, plus `paneDivisionsX × paneDivisionsY` mullion/transom bars, tagged
//     `windowFrame`.
//   - GLASS: one thin box per pane cell near the mid-depth of the frame, tagged `windowGlass` — the
//     renderer marks this slot's material `transparent: true, depthWrite: false` and renders it as
//     a distinct pass.
//   - SILL: an outward-projecting box with a small slope, under the opening on the exterior side,
//     tagged `windowFrame`.
//
// UVs are metre-scaled so procedural materials tile continuously. Pure: no three / mobx-graphlink /
// DOM imports — the Node vitest lane imports this directly.
import {MeshBuilder, BoxMesh, TransformPlanToBasis, type MeshPlan, type V3} from "./MeshPlan.js";

export type WindowModelParams = {
	width: number;
	height: number;
	paneDivisionsX: number;
	paneDivisionsY: number;
	/** Wall thickness (metres) — the reveal depth the frame sits in; the sill projects past the
	 *  exterior face by SILL_OVERHANG. */
	wallThickness: number;
};

/** The planar basis that positions the model in building-local space. `t` = along-wall tangent,
 *  `up` = world up, `n` = wall OUTWARD normal. `pos` = the building-local origin of the model
 *  frame (the inner face at the opening's center x = 0, y = the sill, z = 0). The model's
 *  authoring frame maps x→t (along), y→up, z→n (outward). */
export type OpeningBasis = {
	t: V3;
	n: V3;
	up: V3;
	pos: V3;
};

const CASING_W = 0.06;     // casing / sash stile width in the model frame
const SASH_INSET = 0.003;  // ~3mm reveal from the casing inner edge — reads as a tight joint, not a gap
const GLASS_THICKNESS = 0.004;
const SILL_OVERHANG = 0.04; // sill projection past the OUTER face (toward the exterior)
const SILL_SLOPE = 0.05;   // sill slope (drop, dimensionless)

// ---- Input-validation defaults (bug-I analog): a malformed/absent persisted field can no longer
// silently collapse the geometry (repo "silently-disabled guards" note).
const DEFAULT_WIDTH = 1.2;          // metres
const DEFAULT_HEIGHT = 1.4;         // metres
const DEFAULT_WALL_THICKNESS = 0.2; // metres

/** Resolve a positive, finite dimension; on any invalid input fall back to `fallback`. */
function ResolveDimension(value: number|n, name: string, fallback: number): number {
	return (typeof value == "number" && Number.isFinite(value) && value > 0) ? value : fallback;
}

/** Build the window model in opening-segment-local frame, then place it in building-local space via
 *  `basis`. Groups are tagged windowFrame (novel opaque casing+sash+sill) and windowGlass (the
 *  transparent pane pass). */
export function BuildWindowModel(params: WindowModelParams, basis: OpeningBasis): MeshPlan {
	const local = BuildWindowModelLocal(params);
	return TransformPlanToBasis(local, {t: basis.t, up: basis.up, n: basis.n, pos: basis.pos});
}

/** Build the window model in the opening-segment-local frame only (no placement). Pure +
 *  node-testable. Exposed for the unit tests. */
export function BuildWindowModelLocal(params: WindowModelParams): MeshPlan {
	// Resolve-then-compare input validation (bug I analog), FIRST — every consumer below compares
	// against a validated value, never a raw possibly-undefined/NaN field.
	const w = ResolveDimension(params.width, "width", DEFAULT_WIDTH);
	const h = ResolveDimension(params.height, "height", DEFAULT_HEIGHT);
	const wt = ResolveDimension(params.wallThickness, "wallThickness", DEFAULT_WALL_THICKNESS);
	const dx = Math.max(1, Math.round(params.paneDivisionsX) || 1);
	const dy = Math.max(1, Math.round(params.paneDivisionsY) || 1);
	const b = new MeshBuilder();

	// ---- Frame (4 casings filling the full wallThickness reveal — D1 analog). Head + sill casing
	// span ONLY between the jambs so the four corners are not double-covered (bug F analog).
	b.setMaterialSlot("windowFrame");
	const cw = CASING_W;
	b.append(BoxMesh([-w / 2, 0, 0], [cw, h, wt], "windowFrame"));                      // left jamb
	b.append(BoxMesh([w / 2 - cw, 0, 0], [cw, h, wt], "windowFrame"));                  // right jamb
	b.append(BoxMesh([-w / 2 + cw, h - cw, 0], [w - 2 * cw, cw, wt], "windowFrame"));   // head (between jambs)
	b.append(BoxMesh([-w / 2 + cw, 0, 0], [w - 2 * cw, cw, wt], "windowFrame"));        // sill casing (between jambs)

	// ---- Sash: inner frame ring + mullions/transoms.
	const innerX0 = -w / 2 + cw + SASH_INSET;
	const innerX1 = w / 2 - cw - SASH_INSET;
	const innerY0 = cw + SASH_INSET;
	const innerY1 = h - cw - SASH_INSET;
	const barW = 0.04; // mullion/transom bar thickness
	b.setMaterialSlot("windowFrame");
	// Left / right sash stiles.
	b.append(BoxMesh([innerX0, innerY0, 0], [cw, innerY1 - innerY0, wt], "windowFrame"));
	b.append(BoxMesh([innerX1 - cw, innerY0, 0], [cw, innerY1 - innerY0, wt], "windowFrame"));
	// Top / bottom sash rails — span ONLY between the stiles (bug F analog).
	b.append(BoxMesh([innerX0 + cw, innerY1 - cw, 0], [innerX1 - innerX0 - 2 * cw, cw, wt], "windowFrame"));
	b.append(BoxMesh([innerX0 + cw, innerY0, 0], [innerX1 - innerX0 - 2 * cw, cw, wt], "windowFrame"));

	// ---- Glass panes: one thin box per pane cell, near the mid-depth of the frame.
	const paneW = (innerX1 - innerX0) / dx;
	const paneH = (innerY1 - innerY0) / dy;
	const glassZ = wt / 2 - GLASS_THICKNESS / 2;
	b.setMaterialSlot("windowGlass");
	for (let i = 0; i < dx; i++) {
		for (let j = 0; j < dy; j++) {
			const px = innerX0 + i * paneW;
			const py = innerY0 + j * paneH;
			b.append(BoxMesh([px, py, glassZ], [paneW, paneH, GLASS_THICKNESS], "windowGlass"));
		}
	}

	// ---- Sill: an outward-projecting sloped box under the casing on the exterior side. It spans
	// the opening width, projects just past the outer face, and drops slightly toward the exterior.
	b.setMaterialSlot("windowFrame");
	const sillTop = 0; // flush with the sill casing bottom
	const sillProjZ = wt + SILL_OVERHANG; // extends past the exterior face
	const sillX0 = -w / 2 - 0.01, sillX1 = w / 2 + 0.01;
	const sillThick = 0.025;
	const sillLen = sillX1 - sillX0;
	const sillPlan = BoxMesh([sillX0, sillTop - sillThick, 0], [sillLen, sillThick, sillProjZ], "windowFrame");
	// Apply a gentle slope to the top face by rotating a hair about the along-wall axis (the slope
	// conveys "water runs off the outside edge"). Only a small tilt so it stays a clean box.
	b.append(RotateAlongWall(sillPlan, sillX0, sillTop - sillThick, SILL_SLOPE));

	return b.build();
}

/** Tilt `plan` about the along-wall axis through (originX, originY) by a small `slope` so the sill
 *  top drops toward the exterior. Pure box-preserving rotation (tiny angle). */
function RotateAlongWall(plan: MeshPlan, originX: number, originY: number, slope: number): MeshPlan {
	const angle = -Math.atan(slope);
	const c = Math.cos(angle), s = Math.sin(angle);
	const out: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	const P = plan.positions, N = plan.normals, U = plan.uvs;
	for (let i = 0, k = 0; i < P.length; i += 3, k += 2) {
		const x = P[i], y = P[i + 1], z = P[i + 2];
		const dx = y - originY;
		// rotate (y,z) in the yz-plane about originY.
		const ny = dx * c - z * s + originY;
		const nz = dx * s + z * c;
		out.positions.push(x, ny, nz);
		const vx = N[i], vy = N[i + 1], vz = N[i + 2];
		out.normals.push(vx, vy * c - vz * s, vy * s + vz * c);
		out.uvs.push(U[k], U[k + 1]);
	}
	for (const g of plan.groups) out.groups.push({...g});
	return out;
}
